import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '@/styles/neon-data-grid.css';
import '@/styles/plans-of-study.grid.css';
import { NeonButton, NeonPagination, NeonSearch, NeonTooltip } from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import {
  getPlansOfStudyBreadcrumbs,
  PLANS_OF_STUDY_ID,
  PLANS_OF_STUDY_ICON,
  PLANS_OF_STUDY_TEXT,
  FILTER_STATUS_OPTIONS,
  FILTER_CLASS_YEAR_OPTIONS,
  STATUS_FILTER_VALUES,
  PUBLISHED_FILTER_VALUES,
  EMPTY_PLAN_OF_STUDY_ID,
  EMPTY_PLAN_OF_STUDY_TEXT,
  DEFAULT_PLANS_OF_STUDY_PAGE_SIZE,
  PLANS_OF_STUDY_PAGE_SIZE_OPTIONS,
  PLANS_OF_STUDY_FETCH_PAGE_SIZE,
  PLANS_OF_STUDY_GRID_HEADER_CLASS,
  PLANS_OF_STUDY_GRID_HEADER_CLASS_RIGHT,
} from '@/constants/plan-of-study-constants';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import { usePageConfig } from '@/layout-templates';
import type { PlanOfStudy, PlanOfStudyFilters } from '@/types';
import type {
  ColDef,
  CellStyle,
  FirstDataRenderedEvent,
  IHeaderParams,
  SortChangedEvent,
} from 'ag-grid-community';
import type { FilterField, FilterChip } from '@/components/DataGrid/filter-panel';
import { EmptyPlansOfStudy } from './empty-plans-of-study';
import { NoFilterResults } from '@/components/EmptyState/no-filter-results';
import { DataGrid } from '@/components/DataGrid/data-grid';
import { SortableHeader } from '@/components/sortable-header';
import {
  planCountStyle,
  searchFieldStyle,
  actionButtonsContainerStyle,
  cellTextStyle,
  noDescriptionStyle,
  linkCellStyle,
  skeletonContainerStyle,
  skeletonHeaderStyle,
  skeletonRowStyle,
  skeletonBarStyle,
} from './plans-of-study.styles';
import { useGetPlansOfStudy } from '@/repositories/plans-of-study-repository';
import { plansOfStudyRepository } from '@/repositories/plans-of-study-repository';
import { useGetSchools } from '@/repositories/school-repository';
import { CreatePlanModal } from './create-plan-modal';
import type { CreatePlanModalChoice } from './create-plan-modal';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import { PlanSchoolsMappingDrawer } from './plan-schools-mapping-drawer';
import { PublishAndUpdateDrawer } from './publish-and-update-drawer';
import {
  buildSchedulePublishSuccessToastMessage,
  isPlanScheduleLockedForStaffUi,
  writeScheduledPublishToSession,
  type SchedulePublishSuccessPayload,
} from './publish-schedule-copy';
import { DeletePlanModal } from './delete-plan-modal';
import { isPublishScheduleFeatureEnabled, isPosReadOnly } from '@/utils/cp-context';
import { getClassYearRange } from './create-plan-form-validation';

const PLANS_OF_STUDY_TEXT_NO_DESCRIPTION = 'No description';

const { min: DEFAULT_CLASS_YEAR_FROM_NUM, max: DEFAULT_CLASS_YEAR_TO_NUM } = getClassYearRange();
const DEFAULT_CLASS_YEAR_FROM = String(DEFAULT_CLASS_YEAR_FROM_NUM);
const DEFAULT_CLASS_YEAR_TO = String(DEFAULT_CLASS_YEAR_TO_NUM);

const getClassYearToFilterOptions = (classYearFrom?: string) => {
  const from = classYearFrom ? Number(classYearFrom) : DEFAULT_CLASS_YEAR_FROM_NUM;
  return FILTER_CLASS_YEAR_OPTIONS.filter((option) => Number(option.value) >= from);
};

const normalizeAssignedClassYears = (value: string): string => {
  const trimmed = value.trim();
  const sameYearMatch = trimmed.match(/^(\d{4})\s*[-\u2013]\s*(\d{4})$/);
  if (sameYearMatch && sameYearMatch[1] === sameYearMatch[2]) {
    return sameYearMatch[1];
  }
  return trimmed;
};

const compareCaseInsensitive = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

const DEFAULT_FILTERS: PlanOfStudyFilters = {
  status: STATUS_FILTER_VALUES.EMPTY,
  hideInactive: true,
  publishedSchools: [],
  search: '',
  classYearFrom: DEFAULT_CLASS_YEAR_FROM,
  classYearTo: DEFAULT_CLASS_YEAR_TO,
  published: PUBLISHED_FILTER_VALUES.EMPTY,
  page: 1,
  pageSize: DEFAULT_PLANS_OF_STUDY_PAGE_SIZE,
};

const CREATE_MODAL_ID = 'create-plan-of-study-modal';
const DELETE_MODAL_ID = 'delete-plan-of-study-modal';
const COPY_NAME_PREFIX = 'Copy of ';
const FILTER_STORAGE_KEY = 'pos-list-filters';
const DEFAULT_SORT_BY = 'name';
const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'asc';

/** Drop saved list filters on full reload; keep them for in-app navigation. */
const navEntry = performance.getEntriesByType('navigation')[0] as
  | PerformanceNavigationTiming
  | undefined;
if (navEntry?.type === 'reload') {
  sessionStorage.removeItem(FILTER_STORAGE_KEY);
}

function loadPersistedFilters(): PlanOfStudyFilters | null {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as PlanOfStudyFilters;
  } catch {
    return null;
  }
}

export const PlansOfStudy = (): ReactElement => {
  const navigate = useNavigate();
  const { showToast } = useToastNotifications();
  const isDistrictStaff = !isPosReadOnly();
  const schoolsMappingReadOnly = isPosReadOnly();
  const shouldShowPublishedSchoolsFilter = isDistrictStaff;
  const publishScheduleEnabled = isPublishScheduleFeatureEnabled();

  const [draftFilters, setDraftFilters] = useState<PlanOfStudyFilters>(
    () => loadPersistedFilters() ?? DEFAULT_FILTERS
  );
  const [appliedFilters, setAppliedFilters] = useState<PlanOfStudyFilters>(
    () => loadPersistedFilters() ?? DEFAULT_FILTERS
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [searchKey, setSearchKey] = useState(0);
  const [isSavingSchools, setIsSavingSchools] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => loadPersistedFilters()?.page ?? 1);
  const [pageLength, setPageLength] = useState(
    () => loadPersistedFilters()?.pageSize ?? DEFAULT_PLANS_OF_STUDY_PAGE_SIZE
  );
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

  const {
    data: plansData,
    isLoading,
    isFetching,
    isError,
  } = useGetPlansOfStudy({
    ...appliedFilters,
    page: 1,
    pageSize: PLANS_OF_STUDY_FETCH_PAGE_SIZE,
  });
  const { data: schools = [], isLoading: isSchoolsLoading } = useGetSchools();
  const sortedSchools = useMemo(
    () => [...schools].sort((a, b) => compareCaseInsensitive(a.name, b.name)),
    [schools]
  );
  const rawPlans = useMemo(
    () => (isApplyingFilters || isSavingSchools ? [] : (plansData?.data ?? [])),
    [isApplyingFilters, isSavingSchools, plansData]
  );

  const getPlanSortValue = useCallback((plan: PlanOfStudy, field: string): string | number => {
    switch (field) {
      case 'name':
        return plan.name ?? '';
      case 'credits':
        return plan.credits ?? 0;
      case 'publishedSchools':
        return plan.publishedSchools.length;
      case 'activeSchools':
        return plan.activeSchools.length;
      default:
        return '';
    }
  }, []);

  const plans = useMemo(() => {
    const effectiveSortBy = sortBy ?? DEFAULT_SORT_BY;
    const effectiveSortOrder = sortOrder ?? DEFAULT_SORT_ORDER;

    const direction = effectiveSortOrder === 'asc' ? 1 : -1;

    return [...rawPlans].sort((a, b) => {
      const aVal = getPlanSortValue(a, effectiveSortBy);
      const bVal = getPlanSortValue(b, effectiveSortBy);

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const result = compareCaseInsensitive(aVal, bVal);
        if (result !== 0) {
          return result * direction;
        }
      } else {
        if (aVal < bVal) {
          return -1 * direction;
        }
        if (aVal > bVal) {
          return 1 * direction;
        }
      }

      return compareCaseInsensitive(a.name, b.name);
    });
  }, [getPlanSortValue, rawPlans, sortBy, sortOrder]);
  const displayedPlans = useMemo(() => {
    const start = (currentPage - 1) * pageLength;
    return plans.slice(start, start + pageLength);
  }, [plans, currentPage, pageLength]);
  const totalPlans = plansData?.total ?? 0;
  const [gridReady, setGridReady] = useState(false);
  const gridReadyRef = useRef(false);
  const showSkeleton = !isError && (!plansData || (displayedPlans.length > 0 && !gridReady));

  // Track the order in which filter groups are activated so chips render in selection order
  const chipOrderRef = useRef<string[]>([]);

  const [drawerPlan, setDrawerPlan] = useState<PlanOfStudy | null>(null);
  const [drawerMode, setDrawerMode] = useState<'publish' | 'active' | null>(null);

  const handleDrawerSaveStart = useCallback(() => {
    setIsSavingSchools(true);
    setDrawerPlan(null);
    setDrawerMode(null);
  }, []);

  const handleSchedulePublishFromList = useCallback(
    (payload: SchedulePublishSuccessPayload) => {
      writeScheduledPublishToSession(payload.planId, payload);
      showToast('success', {
        ...buildSchedulePublishSuccessToastMessage(
          payload.planName,
          payload.schoolCount,
          payload.scheduledAt
        ),
      });
    },
    [showToast]
  );

  const deleteMutation = plansOfStudyRepository.useDeletePlanOfStudy();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'new' | 'copy' | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlanOfStudy | null>(null);

  const openCreateModal = useCallback(() => setModalOpen(true), []);
  const closeCreateModal = useCallback(() => {
    setModalOpen(false);
    setSelectedMode(null);
    setSelectedPlanId(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) {
      return;
    }
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast('success', { heading: 'Plan of study deleted' });
      },
      onError: () => {
        showToast('error', { heading: 'Unable to delete plan of study' });
      },
    });
  }, [deleteTarget, deleteMutation, showToast]);

  const handleContinue = useCallback(
    (choice: CreatePlanModalChoice) => {
      closeCreateModal();
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
      if (choice.mode === 'new') {
        navigate(`/${STAFF_ROUTES.plansOfStudy.create}`);
      } else {
        const copyName = `${COPY_NAME_PREFIX}${choice.planName}`;
        navigate(`/${STAFF_ROUTES.plansOfStudy.create}`, {
          state: {
            copyFromPlanId: choice.planId,
            copySuggestedName: copyName,
          },
        });
      }
    },
    [closeCreateModal, navigate]
  );

  const publishedSchoolsOptions = useMemo(
    () => sortedSchools.map((s) => ({ id: s.nid, name: s.nid, labelText: s.name })),
    [sortedSchools]
  );

  const hasFilterChanges = useMemo(
    () => JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters),
    [draftFilters, appliedFilters]
  );

  const handleFilterFieldChange = useCallback((fieldId: string, value: unknown): void => {
    // Determine chip group key for ordering
    let chipGroup: string | null = null;
    if (fieldId === 'published') {
      chipGroup = 'status';
    } else if (fieldId === 'publishedSchools') {
      chipGroup = 'school';
    } else if (fieldId === 'classYearFrom' || fieldId === 'classYearTo') {
      chipGroup = 'classYears';
    }

    if (chipGroup && !chipOrderRef.current.includes(chipGroup)) {
      chipOrderRef.current = [...chipOrderRef.current, chipGroup];
    }

    setDraftFilters((prev) => {
      const next = { ...prev, [fieldId]: value };
      // Ensure classYearFrom is never greater than classYearTo
      if (fieldId === 'classYearTo' && Number(next.classYearFrom) > Number(value)) {
        next.classYearFrom = value as string;
      }
      if (fieldId === 'classYearFrom' && Number(value) > Number(next.classYearTo)) {
        next.classYearTo = value as string;
      }
      return next;
    });
  }, []);

  const handleFilterApply = useCallback((): void => {
    setIsApplyingFilters(true);
    setCurrentPage(1);
    const next = { ...draftFilters, page: 1, pageSize: pageLength };
    setAppliedFilters(next);
    try {
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, [draftFilters, pageLength]);

  useEffect(() => {
    if (isApplyingFilters && !isFetching) {
      setIsApplyingFilters(false);
    }
  }, [isApplyingFilters, isFetching]);

  const classYearToFilterOptions = useMemo(
    () => getClassYearToFilterOptions(draftFilters.classYearFrom),
    [draftFilters.classYearFrom]
  );

  useEffect(() => {
    if (shouldShowPublishedSchoolsFilter) {
      return;
    }

    chipOrderRef.current = chipOrderRef.current.filter((group) => group !== 'school');
    setDraftFilters((prev) =>
      (prev.publishedSchools ?? []).length > 0 ? { ...prev, publishedSchools: [] } : prev
    );
    setAppliedFilters((prev) =>
      (prev.publishedSchools ?? []).length > 0 ? { ...prev, publishedSchools: [] } : prev
    );
  }, [shouldShowPublishedSchoolsFilter]);

  const filterFields = useMemo<FilterField[]>(
    () => [
      ...(isDistrictStaff
        ? [
            {
              type: 'select' as const,
              id: 'published',
              label: PLANS_OF_STUDY_TEXT.FilterStatusLabel,
              value: draftFilters.published ?? PUBLISHED_FILTER_VALUES.EMPTY,
              options: FILTER_STATUS_OPTIONS,
            },
          ]
        : []),
      {
        type: 'checkbox' as const,
        id: 'hideInactive',
        label: PLANS_OF_STUDY_TEXT.FilterHideInactiveLabel,
        checked: draftFilters.hideInactive ?? false,
      },
      ...(shouldShowPublishedSchoolsFilter
        ? [
            {
              type: 'multiselect' as const,
              id: 'publishedSchools',
              label: PLANS_OF_STUDY_TEXT.FilterPublishedSchoolsLabel,
              value: draftFilters.publishedSchools ?? [],
              placeholder: 'All schools',
              options: publishedSchoolsOptions,
            },
          ]
        : []),
      {
        type: 'range' as const,
        fromField: {
          id: 'classYearFrom',
          label: PLANS_OF_STUDY_TEXT.FilterClassYearsLabel,
          value: draftFilters.classYearFrom ?? DEFAULT_CLASS_YEAR_FROM,
          options: FILTER_CLASS_YEAR_OPTIONS,
        },
        toField: {
          id: 'classYearTo',
          label: '',
          showLabel: false,
          value: draftFilters.classYearTo ?? DEFAULT_CLASS_YEAR_TO,
          options: classYearToFilterOptions,
        },
        toLabel: PLANS_OF_STUDY_TEXT.FilterClassYearsTo,
      },
    ],
    [
      classYearToFilterOptions,
      draftFilters,
      isDistrictStaff,
      publishedSchoolsOptions,
      shouldShowPublishedSchoolsFilter,
    ]
  );

  const defaultFilterFields = useMemo<FilterField[]>(
    () => [
      ...(isDistrictStaff
        ? [
            {
              type: 'select' as const,
              id: 'published',
              label: PLANS_OF_STUDY_TEXT.FilterStatusLabel,
              value: DEFAULT_FILTERS.published ?? PUBLISHED_FILTER_VALUES.EMPTY,
              options: FILTER_STATUS_OPTIONS,
            },
          ]
        : []),
      {
        type: 'checkbox' as const,
        id: 'hideInactive',
        label: PLANS_OF_STUDY_TEXT.FilterHideInactiveLabel,
        checked: DEFAULT_FILTERS.hideInactive ?? false,
      },
      ...(shouldShowPublishedSchoolsFilter
        ? [
            {
              type: 'multiselect' as const,
              id: 'publishedSchools',
              label: PLANS_OF_STUDY_TEXT.FilterPublishedSchoolsLabel,
              value: DEFAULT_FILTERS.publishedSchools ?? [],
              placeholder: 'All schools',
              options: publishedSchoolsOptions,
            },
          ]
        : []),
      {
        type: 'range' as const,
        fromField: {
          id: 'classYearFrom',
          label: PLANS_OF_STUDY_TEXT.FilterClassYearsLabel,
          value: DEFAULT_FILTERS.classYearFrom ?? DEFAULT_CLASS_YEAR_FROM,
          options: FILTER_CLASS_YEAR_OPTIONS,
        },
        toField: {
          id: 'classYearTo',
          label: '',
          showLabel: false,
          value: DEFAULT_FILTERS.classYearTo ?? DEFAULT_CLASS_YEAR_TO,
          options: getClassYearToFilterOptions(DEFAULT_FILTERS.classYearFrom),
        },
        toLabel: PLANS_OF_STUDY_TEXT.FilterClassYearsTo,
      },
    ],
    [isDistrictStaff, publishedSchoolsOptions, shouldShowPublishedSchoolsFilter]
  );

  const filterChips = useMemo<FilterChip[]>(() => {
    const chipMap = new Map<string, FilterChip[]>();

    // Status chip
    const publishedVal = draftFilters.published ?? PUBLISHED_FILTER_VALUES.EMPTY;
    if (publishedVal !== PUBLISHED_FILTER_VALUES.EMPTY) {
      const statusOption = FILTER_STATUS_OPTIONS.find((o) => o.value === publishedVal);
      if (statusOption) {
        chipMap.set('status', [{ id: 'status', label: `Status | ${statusOption.text}` }]);
      }
    }

    // School chips
    const selected = draftFilters.publishedSchools ?? [];
    const schoolChips: FilterChip[] = [];
    for (const val of selected) {
      const school = publishedSchoolsOptions.find((o) => o.name === val);
      if (school) {
        schoolChips.push({ id: `school:${val}`, label: `School | ${school.labelText}` });
      }
    }
    if (shouldShowPublishedSchoolsFilter && schoolChips.length > 0) {
      chipMap.set('school', schoolChips);
    }

    // Eligible classes chip
    const defaultFrom = DEFAULT_CLASS_YEAR_FROM;
    const defaultTo = DEFAULT_CLASS_YEAR_TO;
    const fromVal = draftFilters.classYearFrom ?? defaultFrom;
    const toVal = draftFilters.classYearTo ?? defaultTo;
    if (fromVal !== defaultFrom || toVal !== defaultTo) {
      chipMap.set('classYears', [
        { id: 'classYears', label: `Eligible classes | ${fromVal}-${toVal}` },
      ]);
    }

    // Build chips in the order they were selected
    const chips: FilterChip[] = [];
    const seen = new Set<string>();
    for (const group of chipOrderRef.current) {
      if (chipMap.has(group)) {
        chips.push(...chipMap.get(group)!);
        seen.add(group);
      }
    }
    // Append any chips whose group wasn't tracked (e.g. restored from session)
    for (const [group, groupChips] of chipMap) {
      if (!seen.has(group)) {
        chips.push(...groupChips);
      }
    }

    return chips;
  }, [draftFilters, publishedSchoolsOptions, shouldShowPublishedSchoolsFilter]);

  /**
   * Show the filter/action bar when:
   * - there ARE plans in the list (totalPlans > 0), OR
   * - the user has explicitly applied search/drawer filters (hasExplicitFilters), OR
   * - inactive plans exist but are hidden by the default "Hide inactive plans" filter
   *
   * Mirrors course catalog `showLayout` / `hasExplicitFilters` (see course-catalog.tsx).
   * Use all-lite only when the filtered list is empty with hideInactive enabled so a true
   * empty district (zero plans total) still hides the filter chrome.
   */
  const shouldCheckForHiddenInactivePlans =
    Boolean(appliedFilters.hideInactive) &&
    !isLoading &&
    !isFetching &&
    !isError &&
    plansData !== null &&
    totalPlans === 0 &&
    !appliedFilters.search?.trim() &&
    filterChips.length === 0;

  const { data: plansForCopyDropdown = [], isLoading: isPlansLiteLoading } =
    plansOfStudyRepository.usePlansOfStudyListLite(
      (modalOpen && selectedMode === 'copy') || shouldCheckForHiddenInactivePlans
    );

  const hasPlansHiddenByInactiveFilter =
    shouldCheckForHiddenInactivePlans && plansForCopyDropdown.length > 0;

  const hasExplicitFilters =
    filterChips.length > 0 ||
    Boolean(appliedFilters.search?.trim()) ||
    hasPlansHiddenByInactiveFilter;

  const showPosLayout = totalPlans > 0 || hasExplicitFilters;

  const hidePosFilterToggle = useMemo(() => {
    if (isApplyingFilters || isSavingSchools || isLoading || isFetching) {
      return false;
    }
    if (shouldCheckForHiddenInactivePlans && isPlansLiteLoading) {
      return false;
    }
    return !showPosLayout;
  }, [
    isApplyingFilters,
    isFetching,
    isLoading,
    isPlansLiteLoading,
    isSavingSchools,
    shouldCheckForHiddenInactivePlans,
    showPosLayout,
  ]);

  const handleChipRemove = useCallback((chipId: string): void => {
    if (chipId === 'status') {
      chipOrderRef.current = chipOrderRef.current.filter((g) => g !== 'status');
      setDraftFilters((prev) => ({
        ...prev,
        published: PUBLISHED_FILTER_VALUES.EMPTY,
      }));
    } else if (chipId.startsWith('school:')) {
      const schoolId = chipId.replace('school:', '');
      setDraftFilters((prev) => {
        const next = (prev.publishedSchools ?? []).filter((s) => s !== schoolId);
        if (next.length === 0) {
          chipOrderRef.current = chipOrderRef.current.filter((g) => g !== 'school');
        }
        return { ...prev, publishedSchools: next };
      });
    } else if (chipId === 'classYears') {
      chipOrderRef.current = chipOrderRef.current.filter((g) => g !== 'classYears');
      setDraftFilters((prev) => ({
        ...prev,
        classYearFrom: DEFAULT_CLASS_YEAR_FROM,
        classYearTo: DEFAULT_CLASS_YEAR_TO,
      }));
    }
  }, []);

  const handleClearAllChips = useCallback((): void => {
    chipOrderRef.current = [];
    setDraftFilters((prev) => ({
      ...prev,
      published: PUBLISHED_FILTER_VALUES.EMPTY,
      publishedSchools: [],
      classYearFrom: DEFAULT_CLASS_YEAR_FROM,
      classYearTo: DEFAULT_CLASS_YEAR_TO,
    }));
  }, []);

  const handleClearFiltersAndApply = useCallback((): void => {
    chipOrderRef.current = [];
    setCurrentPage(1);
    setPageLength(DEFAULT_PLANS_OF_STUDY_PAGE_SIZE);
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setSearchKey((k) => k + 1);
  }, []);

  const handleSearchChange = useCallback(
    (value: string): void => {
      setDraftFilters((prev) => ({
        ...prev,
        search: value,
      }));

      const shouldApplySearch = value.length === 0 || value.length >= 3;
      if (!shouldApplySearch) {
        return;
      }

      setIsApplyingFilters(true);
      setCurrentPage(1);
      setAppliedFilters((prev) => {
        const next = {
          ...prev,
          search: value,
          page: 1,
          pageSize: pageLength,
        };
        try {
          sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [pageLength]
  );

  const columnDefs = useMemo<ColDef<PlanOfStudy>[]>(() => {
    const baseCellStyle: CellStyle = {
      display: 'flex',
      alignItems: 'center',
      padding: '0 0.5rem',
    };

    const baseColumns: ColDef<PlanOfStudy>[] = [
      {
        field: 'name',
        headerName: 'Plan name',
        width: 240,
        minWidth: 120,
        headerClass: 'plans-of-study-header plans-of-study-header-left',
        cellStyle: baseCellStyle,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        comparator: (valueA, valueB) =>
          compareCaseInsensitive(String(valueA ?? ''), String(valueB ?? '')),
        headerComponent: SortableHeader,
        cellRenderer: (props: { data: PlanOfStudy }) => {
          const plan = props.data;
          if (!plan?.id) {
            return <span>{plan?.name ?? ''}</span>;
          }
          return (
            <Link
              to={`/${STAFF_ROUTES.plansOfStudy.detail(plan.id)}`}
              style={{ ...linkCellStyle, cursor: 'pointer' }}
              data-testid={`plan-name-link-${plan.id}`}
            >
              {plan.name ?? ''}
            </Link>
          );
        },
      },
      {
        field: 'credits',
        headerName: 'Credits',
        width: 120,
        minWidth: 110,
        headerClass: PLANS_OF_STUDY_GRID_HEADER_CLASS_RIGHT,
        cellStyle: { ...baseCellStyle, justifyContent: 'center', textAlign: 'center' },
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
        cellRenderer: (props: {
          data: PlanOfStudy & { requirements?: Array<{ credits: number }> };
        }) => {
          const d = props.data;
          if (typeof d.credits === 'number') {
            return <span style={{ width: '100%', textAlign: 'center' }}>{d.credits}</span>;
          }
          if (Array.isArray(d.requirements)) {
            const total = d.requirements.reduce((sum, r) => sum + (r.credits || 0), 0);
            return (
              <span style={{ width: '100%', textAlign: 'center' }}>
                {parseFloat(total.toFixed(2))}
              </span>
            );
          }
          return <span style={{ width: '100%', textAlign: 'center' }}>0</span>;
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 420,
        headerClass: 'plans-of-study-header plans-of-study-header-left',
        cellStyle: { ...baseCellStyle, justifyContent: 'flex-start', textAlign: 'left' },
        sortable: false,
        cellRenderer: (props: { data: PlanOfStudy & { descriptionPlainText?: string } }) => {
          const desc = props.data.description || props.data.descriptionPlainText || '';
          if (!desc) {
            return <span style={noDescriptionStyle}>{PLANS_OF_STUDY_TEXT_NO_DESCRIPTION}</span>;
          }
          return <span style={cellTextStyle}>{desc}</span>;
        },
      },
      {
        field: 'assignedClassYears',
        headerName: 'Assigned class years',
        width: 210,
        minWidth: 190,
        headerClass: `${PLANS_OF_STUDY_GRID_HEADER_CLASS} neon-data-grid-header--static`,
        cellStyle: { ...baseCellStyle, justifyContent: 'center', textAlign: 'center' },
        sortable: false,
        cellRenderer: (props: {
          data: PlanOfStudy & { classYearFrom?: number; classYearTo?: number };
        }) => {
          const d = props.data;
          if (d.assignedClassYears) {
            return (
              <span style={{ width: '100%', textAlign: 'center' }}>
                {normalizeAssignedClassYears(d.assignedClassYears)}
              </span>
            );
          }
          if (d.classYearFrom !== undefined && d.classYearTo !== undefined) {
            return (
              <span style={{ width: '100%', textAlign: 'center' }}>
                {d.classYearFrom === d.classYearTo
                  ? d.classYearFrom
                  : `${d.classYearFrom} - ${d.classYearTo}`}
              </span>
            );
          }
          return <span style={{ width: '100%', textAlign: 'center' }}>-</span>;
        },
      },
      {
        field: 'publishedSchools',
        headerName: 'Published schools',
        width: 240,
        minWidth: 220,
        headerClass: PLANS_OF_STUDY_GRID_HEADER_CLASS_RIGHT,
        cellStyle: { ...baseCellStyle, justifyContent: 'center' },
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        cellDataType: false,
        valueGetter: (p) => p.data?.publishedSchools?.length ?? 0,
        headerComponent: (props: IHeaderParams) => (
          <SortableHeader {...props}>
            <span
              role="presentation"
              style={{ display: 'inline-flex', alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <NeonButton
                id={PLANS_OF_STUDY_ID.PublishedSchoolsInfoTooltip}
                dataText="Published schools info"
                dataShowText="false"
                dataType={NeonButtonTypes.Utility}
                dataIcon="circle-info"
                dataAriaLabel="Published schools information"
              />
              <NeonTooltip dataTargetId={PLANS_OF_STUDY_ID.PublishedSchoolsInfoTooltip}>
                {PLANS_OF_STUDY_TEXT.PublishedSchoolsTooltip}
              </NeonTooltip>
            </span>
          </SortableHeader>
        ),
        cellRenderer: (props: { data: PlanOfStudy }) => {
          const scheduleLocked = isPlanScheduleLockedForStaffUi(
            publishScheduleEnabled,
            props.data.id,
            props.data.checkedToPublish
          );
          const count = props.data.publishedSchools.length;
          if (scheduleLocked) {
            return (
              <>
                <span
                  id={`plans-of-study-published-count-${props.data.id}`}
                  style={{ width: '100%', textAlign: 'center' }}
                >
                  {count}
                </span>
                <NeonTooltip dataTargetId={`plans-of-study-published-count-${props.data.id}`}>
                  {PLANS_OF_STUDY_TEXT.ScheduledPublishEditUnavailable}
                </NeonTooltip>
              </>
            );
          }
          return (
            <span
              id={`plans-of-study-published-count-${props.data.id}`}
              style={linkCellStyle}
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              aria-label={`Open Publish and update for ${props.data.name}`}
              onClick={() => {
                setDrawerPlan(props.data);
                setDrawerMode('publish');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setDrawerPlan(props.data);
                  setDrawerMode('publish');
                }
              }}
            >
              {count}
            </span>
          );
        },
      },
      {
        field: 'activeSchools',
        headerName: 'Active schools',
        width: 210,
        minWidth: 190,
        headerClass: PLANS_OF_STUDY_GRID_HEADER_CLASS_RIGHT,
        cellStyle: { ...baseCellStyle, justifyContent: 'center' },
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        cellDataType: false,
        valueGetter: (p) => p.data?.activeSchools?.length ?? 0,
        headerComponent: (props: IHeaderParams) => (
          <SortableHeader {...props}>
            <span
              role="presentation"
              style={{ display: 'inline-flex', alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <NeonButton
                id={PLANS_OF_STUDY_ID.ActiveSchoolsInfoTooltip}
                dataText="Active schools info"
                dataShowText="false"
                dataType={NeonButtonTypes.Utility}
                dataIcon="circle-info"
                dataAriaLabel="Active schools information"
              />
              <NeonTooltip dataTargetId={PLANS_OF_STUDY_ID.ActiveSchoolsInfoTooltip}>
                {PLANS_OF_STUDY_TEXT.ActiveSchoolsTooltip}
              </NeonTooltip>
            </span>
          </SortableHeader>
        ),
        cellRenderer: (props: { data: PlanOfStudy }) => {
          const scheduleLocked = isPlanScheduleLockedForStaffUi(
            publishScheduleEnabled,
            props.data.id,
            props.data.checkedToPublish
          );
          const count = props.data.activeSchools.length;
          const publishedCount = props.data.publishedSchools.length;
          if (scheduleLocked) {
            return (
              <>
                <span
                  id={`plans-of-study-active-count-${props.data.id}`}
                  style={{ width: '100%', textAlign: 'center' }}
                >
                  {count}
                </span>
                <NeonTooltip dataTargetId={`plans-of-study-active-count-${props.data.id}`}>
                  {PLANS_OF_STUDY_TEXT.ScheduledPublishEditUnavailable}
                </NeonTooltip>
              </>
            );
          }
          if (count > 0 || publishedCount > 0) {
            return (
              <span
                id={`plans-of-study-active-count-${props.data.id}`}
                style={linkCellStyle}
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                aria-label={`Open active schools for ${props.data.name}`}
                onClick={() => {
                  setDrawerPlan(props.data);
                  setDrawerMode('active');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setDrawerPlan(props.data);
                    setDrawerMode('active');
                  }
                }}
              >
                {count}
              </span>
            );
          }
          return <span>{count}</span>;
        },
      },
    ];

    // Only include Actions column for district staff
    if (!isPosReadOnly()) {
      baseColumns.push({
        colId: 'actions',
        headerName: 'Actions',
        width: 84,
        minWidth: 84,
        headerClass: `${PLANS_OF_STUDY_GRID_HEADER_CLASS} neon-data-grid-header--static`,
        cellStyle: baseCellStyle,
        sortable: false,
        cellRenderer: (props: { data: PlanOfStudy }) => {
          const scheduleLocked = isPlanScheduleLockedForStaffUi(
            publishScheduleEnabled,
            props.data.id,
            props.data.checkedToPublish
          );
          return (
            <div style={actionButtonsContainerStyle}>
              {scheduleLocked ? (
                <>
                  <span id={`edit-plan-locked-wrap-${props.data.id}`}>
                    <NeonButton
                      id={`edit-plan-${props.data.id}`}
                      dataIcon="edit"
                      dataType="utility"
                      dataAriaLabel={`Edit ${props.data.name} plan of study`}
                      dataText="Edit"
                      dataShowText="false"
                      disabled
                    />
                  </span>
                  <NeonTooltip dataTargetId={`edit-plan-locked-wrap-${props.data.id}`}>
                    {PLANS_OF_STUDY_TEXT.ScheduledPublishEditUnavailable}
                  </NeonTooltip>
                </>
              ) : (
                <>
                  <NeonButton
                    id={`edit-plan-${props.data.id}`}
                    dataIcon="edit"
                    dataType="utility"
                    dataAriaLabel={`Edit ${props.data.name} plan of study`}
                    dataText="Edit"
                    dataShowText="false"
                    onClick={(): void => {
                      navigate(`/${STAFF_ROUTES.plansOfStudy.edit(props.data.id)}`);
                    }}
                  />
                  <NeonTooltip dataTargetId={`edit-plan-${props.data.id}`}>Edit</NeonTooltip>
                </>
              )}
              {props.data.isPublished ? (
                <span id={`delete-plan-tooltip-target-${props.data.id}`}>
                  <NeonButton
                    id={`delete-plan-${props.data.id}`}
                    dataIcon="delete"
                    dataType="utility"
                    dataAriaLabel={`Delete ${props.data.name} plan of study`}
                    dataText="Delete"
                    dataShowText="false"
                    disabled
                  />
                  <NeonTooltip dataTargetId={`delete-plan-tooltip-target-${props.data.id}`}>
                    Published plans cannot be deleted.
                  </NeonTooltip>
                </span>
              ) : scheduleLocked ? (
                <>
                  <span id={`delete-plan-schedule-locked-${props.data.id}`}>
                    <NeonButton
                      id={`delete-plan-${props.data.id}`}
                      dataIcon="delete"
                      dataType="utility"
                      dataAriaLabel={`Delete ${props.data.name} plan of study`}
                      dataText="Delete"
                      dataShowText="false"
                      disabled
                    />
                  </span>
                  <NeonTooltip dataTargetId={`delete-plan-schedule-locked-${props.data.id}`}>
                    {PLANS_OF_STUDY_TEXT.ScheduledPublishEditUnavailable}
                  </NeonTooltip>
                </>
              ) : (
                <span id={`delete-plan-enabled-tooltip-target-${props.data.id}`}>
                  <NeonButton
                    id={`delete-plan-${props.data.id}`}
                    dataIcon="delete"
                    dataType="utility"
                    dataAriaLabel={`Delete ${props.data.name} plan of study`}
                    dataText="Delete"
                    dataShowText="false"
                    onClick={(): void => {
                      setDeleteTarget(props.data);
                    }}
                  />
                  <NeonTooltip dataTargetId={`delete-plan-enabled-tooltip-target-${props.data.id}`}>
                    Delete
                  </NeonTooltip>
                </span>
              )}
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [navigate, publishScheduleEnabled]);

  usePageConfig({
    breadcrumbs: getPlansOfStudyBreadcrumbs,
    breadcrumbsId: PLANS_OF_STUDY_ID.Breadcrumbs,
    title: PLANS_OF_STUDY_TEXT.Title,
    ...(isDistrictStaff && {
      action: {
        id: PLANS_OF_STUDY_ID.Button,
        text: PLANS_OF_STUDY_TEXT.Button,
        icon: PLANS_OF_STUDY_ICON.Add,
        type: NeonButtonTypes.Primary,
        ariaHasPopup: 'dialog',
        onClick: openCreateModal,
      },
    }),
  });

  const handleFirstDataRendered = useCallback((event?: FirstDataRenderedEvent<PlanOfStudy>) => {
    if (event?.api) {
      const hasExistingSort = event.api
        .getColumnState()
        .some((state) => state.sort === 'asc' || state.sort === 'desc');
      if (!hasExistingSort) {
        event.api.applyColumnState({
          state: [{ colId: DEFAULT_SORT_BY, sort: DEFAULT_SORT_ORDER }],
          defaultState: { sort: null },
        });
      }
      setSortBy(DEFAULT_SORT_BY);
      setSortOrder(DEFAULT_SORT_ORDER);
    }

    if (!gridReadyRef.current) {
      gridReadyRef.current = true;
      setGridReady(true);
    }
  }, []);

  const handleGridSortChanged = useCallback((event: SortChangedEvent<PlanOfStudy>) => {
    const sortedState = event.api
      .getColumnState()
      .filter((state) => state.sort === 'asc' || state.sort === 'desc');

    if (sortedState.length > 0) {
      const first = sortedState[0];
      setSortBy(first.colId ?? null);
      setSortOrder(first.sort === 'asc' ? 'asc' : 'desc');
    } else {
      setSortBy(DEFAULT_SORT_BY);
      setSortOrder(DEFAULT_SORT_ORDER);
    }

    setCurrentPage(1);
  }, []);

  const skeletonContent = useMemo(() => {
    const skeletonRows = Array.from({ length: 8 }, (_, i) => i);
    return (
      <div style={skeletonContainerStyle} data-testid="plans-of-study-skeleton">
        <div style={skeletonHeaderStyle}>
          <span style={skeletonBarStyle('10rem')} />
          <span style={skeletonBarStyle('4rem')} />
          <span style={{ ...skeletonBarStyle('100%'), flex: 1 }} />
          <span style={skeletonBarStyle('10rem')} />
          <span style={skeletonBarStyle('8rem')} />
          <span style={skeletonBarStyle('7rem')} />
          <span style={skeletonBarStyle('4rem')} />
        </div>
        {skeletonRows.map((i) => (
          <div key={i} style={skeletonRowStyle}>
            <span style={skeletonBarStyle('10rem')} />
            <span style={skeletonBarStyle('2.5rem')} />
            <span style={{ ...skeletonBarStyle(i % 2 === 0 ? '80%' : '60%'), flex: 1 }} />
            <span style={skeletonBarStyle('7rem')} />
            <span style={skeletonBarStyle('3rem')} />
            <span style={skeletonBarStyle('3rem')} />
            <span style={skeletonBarStyle('3.5rem')} />
          </div>
        ))}
      </div>
    );
  }, []);

  return (
    <>
      {showSkeleton && <div style={{ position: 'relative', zIndex: 1 }}>{skeletonContent}</div>}
      <div
        style={
          showSkeleton
            ? { position: 'absolute', left: '-9999px', visibility: 'hidden', width: '100%' }
            : undefined
        }
      >
        <DataGrid<PlanOfStudy>
          id="plans-of-study"
          columnDefs={columnDefs}
          rowData={displayedPlans}
          domLayout="normal"
          gridHeight="min(600px, 70vh)"
          cardWrapper={true}
          childrenInsideCard={true}
          gridOptions={{
            onFirstDataRendered: handleFirstDataRendered,
            onSortChanged: handleGridSortChanged,
            defaultColDef: { resizable: false },
          }}
          emptyState={
            isApplyingFilters || isSavingSchools ? (
              skeletonContent
            ) : showSkeleton ? (
              <></>
            ) : appliedFilters.search ? (
              <NoFilterResults
                title={EMPTY_PLAN_OF_STUDY_TEXT.NoFilterResultsTitle}
                body={EMPTY_PLAN_OF_STUDY_TEXT.NoFilterResultsBody}
                buttonText={EMPTY_PLAN_OF_STUDY_TEXT.NoFilterResultsButton}
                buttonId={EMPTY_PLAN_OF_STUDY_ID.ClearFiltersButton}
                onClearFilters={handleClearFiltersAndApply}
                showAction={false}
              />
            ) : (
              <EmptyPlansOfStudy
                onCreatePlanOfStudy={isDistrictStaff ? openCreateModal : undefined}
              />
            )
          }
          emptyStateWhenFiltered={
            isApplyingFilters || isSavingSchools ? (
              skeletonContent
            ) : showSkeleton ? (
              <></>
            ) : (
              <NoFilterResults
                title={EMPTY_PLAN_OF_STUDY_TEXT.NoFilterResultsTitle}
                body={EMPTY_PLAN_OF_STUDY_TEXT.NoFilterResultsBody}
                buttonText={EMPTY_PLAN_OF_STUDY_TEXT.NoFilterResultsButton}
                buttonId={EMPTY_PLAN_OF_STUDY_ID.ClearFiltersButton}
                onClearFilters={handleClearFiltersAndApply}
                showAction={false}
              />
            )
          }
          className="neon-data-grid plans-of-study-grid"
          emptyRowsTreatAsFiltered={hasExplicitFilters}
          isFilterOpen={isFilterOpen}
          onFilterOpenChange={setIsFilterOpen}
          filter={{
            title: PLANS_OF_STUDY_TEXT.Filters,
            fields: filterFields,
            chips: filterChips,
            filterCount: filterChips.length + (draftFilters.hideInactive ? 1 : 0),
            onFieldChange: handleFilterFieldChange,
            onChipRemove: handleChipRemove,
            onClearAllChips: handleClearAllChips,
            onApply: handleFilterApply,
            hasChanges: hasFilterChanges && !isApplyingFilters,
            defaultFields: defaultFilterFields,
            applyText: PLANS_OF_STUDY_TEXT.FilterApply,
            closeButtonId: PLANS_OF_STUDY_ID.FilterCancelButton,
            applyButtonId: PLANS_OF_STUDY_ID.FilterApplyButton,
            toggleButtonId: PLANS_OF_STUDY_ID.FiltersButton,
            toggleButtonText: PLANS_OF_STUDY_TEXT.Filters,
            toggleButtonIcon: PLANS_OF_STUDY_ICON.Filters,
            hideToggleButton: hidePosFilterToggle,
            hideActionBar: hidePosFilterToggle,
          }}
          actionBar={
            <>
              <span style={planCountStyle}>
                {totalPlans} {totalPlans === 1 ? 'plan' : 'plans'}
              </span>
              <div style={searchFieldStyle}>
                <NeonSearch
                  key={searchKey}
                  id={PLANS_OF_STUDY_ID.SearchField}
                  dataPlaceholderText={PLANS_OF_STUDY_TEXT.SearchPlaceholder}
                  neonSearchValueChange={handleSearchChange}
                />
              </div>
            </>
          }
        >
          {!isApplyingFilters && !isSavingSchools && totalPlans > 0 && (
            <form onSubmit={(e) => e.preventDefault()} style={{ display: 'contents' }}>
              <div onClickCapture={(e) => e.preventDefault()}>
                <NeonPagination
                  id="plans-of-study-pagination"
                  dataArrayLength={plans.length}
                  dataCurrentPage={currentPage}
                  dataPageLength={pageLength}
                  dataPageSizeOptions={PLANS_OF_STUDY_PAGE_SIZE_OPTIONS}
                  dataShowJumpTo={true}
                  dataShowResults={true}
                  neonPaginationChange={(newPage, newPageLength) => {
                    setCurrentPage(newPage);
                    setPageLength(newPageLength);
                  }}
                />
              </div>
            </form>
          )}
        </DataGrid>
      </div>
      <CreatePlanModal
        isOpen={modalOpen}
        modalId={CREATE_MODAL_ID}
        selectedMode={selectedMode}
        selectedPlanId={selectedPlanId}
        plans={plansForCopyDropdown}
        isPlansLoading={isPlansLiteLoading}
        onClose={closeCreateModal}
        onContinue={handleContinue}
        onModeChange={setSelectedMode}
        onPlanSelect={setSelectedPlanId}
      />
      <DeletePlanModal
        isOpen={!!deleteTarget}
        modalId={DELETE_MODAL_ID}
        planName={deleteTarget?.name ?? ''}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />
      {drawerPlan && drawerMode === 'publish' && (
        <PublishAndUpdateDrawer
          isOpen
          onClose={() => {
            setDrawerPlan(null);
            setDrawerMode(null);
          }}
          planId={drawerPlan.id}
          planName={drawerPlan.name}
          publishedSchools={drawerPlan.publishedSchools}
          activeSchools={drawerPlan.activeSchools}
          allSchoolsSorted={sortedSchools}
          isSchoolsLoading={isSchoolsLoading}
          publishScheduleEnabled={publishScheduleEnabled}
          onSchedulePublish={publishScheduleEnabled ? handleSchedulePublishFromList : undefined}
          readOnly={schoolsMappingReadOnly}
        />
      )}
      {drawerPlan && drawerMode === 'active' && (
        <PlanSchoolsMappingDrawer
          planId={drawerPlan.id}
          planName={drawerPlan.name}
          mode="active"
          publishedSchools={drawerPlan.publishedSchools}
          activeSchools={drawerPlan.activeSchools}
          allSchoolsSorted={sortedSchools}
          isSchoolsLoading={isSchoolsLoading}
          readOnly={schoolsMappingReadOnly}
          onSaveStart={schoolsMappingReadOnly ? undefined : handleDrawerSaveStart}
          onSaveEnd={schoolsMappingReadOnly ? undefined : () => setIsSavingSchools(false)}
          onClose={() => {
            setDrawerPlan(null);
            setDrawerMode(null);
          }}
        />
      )}
    </>
  );
};
